import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatBrandMoney, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  SAUDI_BUDGET_TIERS,
  formatHrDate,
  formatHrMoneyCompact,
  getHrDateLocale,
  getHrPhonePlaceholder,
} from './utils/hrSaudiFormat';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Building, Search, Plus, Edit, Trash2, RefreshCcw, Users, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, Gauge, FileText, Clock, List, LayoutList, Check, UserCheck, Banknote, TrendingUp, Briefcase } from 'lucide-react';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  buildHrColumnVisibilityState,
  buildHrQawaemColumnDefs,
  buildHrImportExportProps,
  buildHrControlsStrip,
  isHrColumnVisible,
  createHrHandleToggleColumn,
  hrTableColSpan,
  useHrModalBodyLock,
  HrFormModalTabBar,
  useHrTableRowSelection,
  HrBulkDeleteFilterAction,
  useHrDepartments,
  useHrEmployees,
} from './hrListPageUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';

import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { generateEntityReference } from '../../shared/utils/generateEntityReference';
import { mergeStickySelectClass, QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';
import {
  dispatchHrDepartmentsRefresh,
} from './hrRefreshEvents';

registerLocale('ar', ar);

const HR_TABLE_COLUMNS = [
  { key: 'code', labelKey: 'col_code' },
  { key: 'name', labelKey: 'col_name' },
  { key: 'manager', labelKey: 'col_manager' },
  { key: 'location', labelKey: 'col_location' },
  { key: 'employeeCount', labelKey: 'col_employees' },
  { key: 'budget', labelKey: 'col_budget' },
  { key: 'createdDate', labelKey: 'col_created_date' },
  { key: 'status', labelKey: 'col_status' },
  { key: 'actions', label: 'actions', required: true },
];

/** Prefix from department name (no generic DEPT mock). */
function getDepartmentCodePrefix(name) {
  const nameUpper = (name || '').toUpperCase().trim();
  if (!nameUpper) return '';
  const words = nameUpper.split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  if (words.length === 1) {
    const w = words[0];
    return w.length <= 4 ? w : w.substring(0, 4);
  }
  return words
    .map((w) => w[0])
    .join('')
    .substring(0, 4);
}


/** Next code like RH-001 from name + existing departments */
function suggestNextDepartmentCode(name, departmentsList) {
  const prefix = getDepartmentCodePrefix(name);
  if (!prefix) return '';
  return generateEntityReference(prefix, departmentsList, (d) => d.code);
}

const DepartmentsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.ressources_humaines.pages.departmentsPage.${key}`, opts);
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getHrDateLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const phonePlaceholder = getHrPhonePlaceholder();

  const formatBudgetDisplay = (val) => {
    if (val == null || val === '') return null;
    const n = parseFloat(val);
    if (Number.isNaN(n)) return p('na');
    return formatBrandMoney(n);
  };

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnStatusButton = event.target.closest('[data-department-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-department-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const { departments: rawDepartments, refresh: refreshDepartments } = useHrDepartments({ includeInactive: true });
  const { employees, refresh: refreshEmployees } = useHrEmployees();
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  useHrModalBodyLock(showModal, showViewModal);
  
  const [formTab, setFormTab] = useState('general');
  
  const [formData, setFormData] = useState({ 
    name: '',
    code: '',
    description: '',
    manager: '',
    location: '',
    email: '',
    phone: '',
    budget: '',
    budgetUsed: '',
    employeeCapacity: '',
    establishedDate: '',
    status: 'active',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_departments');
  const [quickFilter, setQuickFilter] = useState('all');
  
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  /** Map SQLite / API snake_case to form + list camelCase */
  const normalizeDepartment = (d) => {
    if (!d || typeof d !== 'object') return d;
    return {
      ...d,
      manager: d.manager ?? d.manager_id ?? d.manager_name ?? '',
      employeeCapacity: d.employee_capacity ?? d.employee_count ?? d.employeeCapacity ?? 0,
      budgetUsed: d.budget_used ?? d.budgetUsed ?? 0,
      establishedDate: d.established_date ?? d.establishedDate ?? d.created_at ?? '',
      email: d.email ?? '',
      phone: d.phone ?? '',
      notes: d.notes ?? ''
    };
  };

  const departments = useMemo(
    () => sortRecordsNewestFirst(rawDepartments.map(normalizeDepartment)),
    [rawDepartments],
  );

  const refreshDepartmentsData = async () => {
    await Promise.all([refreshDepartments(), refreshEmployees()]);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      
      if (!event.target.closest('.relative')) {      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Count employees linked by department id or name (EmployeesPage stores id in affectation)
  const getEmployeeCount = (deptOrName) => {
    const dept = typeof deptOrName === 'object'
      ? deptOrName
      : departments.find((d) => d.name === deptOrName || d.id === deptOrName);
    const id = dept?.id ?? (typeof deptOrName === 'string' ? deptOrName : '');
    const name = dept?.name ?? (typeof deptOrName === 'string' ? deptOrName : '');
    return employees.filter((e) => e.affectation && (e.affectation === id || e.affectation === name)).length;
  };

  const stats = {
    total: departments.length,
    active: departments.filter(d => d.status === 'active').length,
    inactive: departments.filter(d => d.status === 'inactive').length,
    totalEmployees: employees.length,
    totalBudget: departments.reduce((sum, d) => sum + (parseFloat(d.budget) || 0), 0)
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_departments', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_taille', name: p('tab_by_size'), icon: Users },
    { id: 'par_budget', name: p('tab_by_budget'), icon: Banknote },
  ]), [t, p]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'active', name: p('filter_active'), icon: CheckCircle },
    { id: 'inactive', name: p('filter_inactive'), icon: XCircle },
    { id: 'large', name: p('filter_large'), icon: TrendingUp },
  ]), [p]);

  const departmentsDistribution = useMemo(() => ({
    all_departments: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'inactive', name: p('dist_inactive'), count: stats.inactive, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'with_budget', name: p('dist_with_budget'), count: departments.filter(d => (parseFloat(d.budget) || 0) > 0).length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      ],
    },
    par_taille: {
      title: p('dist_by_size_title'),
      data: [
        {
          filterKey: 'small',
          name: p('dist_small'),
          count: departments.filter(d => {
            const count = getEmployeeCount(d.name);
            return count >= 1 && count <= 10;
          }).length,
          color: 'bg-gradient-to-r from-blue-500 to-blue-600',
        },
        {
          filterKey: 'medium',
          name: p('dist_medium'),
          count: departments.filter(d => {
            const count = getEmployeeCount(d.name);
            return count >= 11 && count <= 30;
          }).length,
          color: 'bg-gradient-to-r from-purple-500 to-purple-600',
        },
        {
          filterKey: 'large',
          name: p('dist_large'),
          count: departments.filter(d => {
            const count = getEmployeeCount(d.name);
            return count >= 31;
          }).length,
          color: 'bg-gradient-to-r from-amber-500 to-amber-600',
        },
      ],
    },
    par_budget: {
      title: p('dist_by_budget_title'),
      data: [
        {
          filterKey: 'budget_low',
          name: p('dist_budget_low'),
          count: departments.filter(d => (parseFloat(d.budget) || 0) < SAUDI_BUDGET_TIERS.lowMax).length,
          color: 'bg-gradient-to-r from-blue-500 to-blue-600',
        },
        {
          filterKey: 'budget_mid',
          name: p('dist_budget_mid'),
          count: departments.filter(d => {
            const budget = parseFloat(d.budget) || 0;
            return budget >= SAUDI_BUDGET_TIERS.lowMax && budget <= SAUDI_BUDGET_TIERS.midMax;
          }).length,
          color: 'bg-gradient-to-r from-green-500 to-green-600',
        },
        {
          filterKey: 'budget_high',
          name: p('dist_budget_high'),
          count: departments.filter(d => (parseFloat(d.budget) || 0) > SAUDI_BUDGET_TIERS.midMax).length,
          color: 'bg-gradient-to-r from-purple-500 to-purple-600',
        },
      ],
    },
  }), [p, stats, departments, employees]);

  const distributionInfo = departmentsDistribution[mainViewMode] || departmentsDistribution.all_departments;

  const baseFilteredDepartments = departments.filter(d => {
    // Search filter
    if (searchTerm && !(
      d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.manager?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.location?.toLowerCase().includes(searchTerm.toLowerCase())
    )) return false;
    
    // Quick filter (only for Vue Générale)
    if (mainViewMode === 'all_departments') {
      if (quickFilter === 'active' && d.status !== 'active') return false;
      if (quickFilter === 'inactive' && d.status !== 'inactive') return false;
      if (quickFilter === 'large') {
        const empCount = getEmployeeCount(d.name);
        if (empCount < 31) return false;
      }
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_departments':
          if (activeDistributionFilter === 'active' && d.status !== 'active') return false;
          if (activeDistributionFilter === 'inactive' && d.status !== 'inactive') return false;
          if (activeDistributionFilter === 'with_budget' && (parseFloat(d.budget) || 0) <= 0) return false;
          break;
        case 'par_taille':
          const empCount = getEmployeeCount(d.name);
          if (activeDistributionFilter === 'small' && (empCount < 1 || empCount > 10)) return false;
          if (activeDistributionFilter === 'medium' && (empCount < 11 || empCount > 30)) return false;
          if (activeDistributionFilter === 'large' && empCount < 31) return false;
          break;
        case 'par_budget':
          const budget = parseFloat(d.budget) || 0;
          if (activeDistributionFilter === 'budget_low' && budget >= SAUDI_BUDGET_TIERS.lowMax) return false;
          if (activeDistributionFilter === 'budget_mid' && (budget < SAUDI_BUDGET_TIERS.lowMax || budget > SAUDI_BUDGET_TIERS.midMax)) return false;
          if (activeDistributionFilter === 'budget_high' && budget <= SAUDI_BUDGET_TIERS.midMax) return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('code', (r) => r.code || ''),
    textColumnRule('name', (r) => r.name || ''),
    textColumnRule('manager', (r) => r.manager || ''),
    textColumnRule('employeeCount', (r) => String(getEmployeeCount(r.name))),
    textColumnRule('budget', (r) => (r.budget ? formatBudgetDisplay(r.budget) : '')),
    exactColumnRule('status', (r) => r.status || ''),
  ], [departments, employees]);

  const filteredDepartments = useMemo(
    () => applyColumnFilters(baseFilteredDepartments, colFilters, columnFilterRules),
    [baseFilteredDepartments, colFilters, columnFilterRules],
  );

  const { allRowsSelected, someRowsSelected, handleSelectAll, toggleRecord: handleSelectRecord } =
    useHrTableRowSelection(filteredDepartments, selectedRecords, setSelectedRecords);

  const handleManagerSelect = (value) => {
    // Find if the selected value matches an existing employee
    const selectedEmployee = employees.find(emp => 
      `${emp.prenom} ${emp.nom}` === value
    );
    
    if (selectedEmployee) {
      // Auto-fill email, phone, and location from employee data
      setFormData({
        ...formData,
        manager: value,
        email: selectedEmployee.email || formData.email,
        phone: selectedEmployee.telephone || formData.phone,
        location: selectedEmployee.ville || selectedEmployee.adresse || formData.location
      });
    } else {
      // Manual entry - just set the manager name
      setFormData({ ...formData, manager: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let departmentCode = (formData.code || '').trim();
      if (!editingRecord && !departmentCode && (formData.name || '').trim()) {
        departmentCode = suggestNextDepartmentCode(formData.name, departments);
      }
      if (!editingRecord && !departmentCode.trim()) {
        alert(p('alert_code_required'));
        return;
      }
      
      // Body must match POST/PUT management_departments (Express route)
      const submitData = {
        name: formData.name,
        code: departmentCode,
        description: formData.description || '',
        manager: formData.manager || '',
        location: formData.location || '',
        email: formData.email || '',
        phone: formData.phone || '',
        budget: parseFloat(formData.budget) || 0,
        budgetUsed: parseFloat(formData.budgetUsed) || 0,
        employeeCapacity: parseInt(formData.employeeCapacity, 10) || 0,
        establishedDate: formData.establishedDate || '',
        status: formData.status || 'active',
        notes: formData.notes || ''
      };

      if (editingRecord) {
        await localApiAdapter.put(`/management/departments/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'القسم' }));
      } else {
        await localApiAdapter.post('/management/departments', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'القسم' }));
      }
      resetForm();
      refreshDepartmentsData();
      dispatchHrDepartmentsRefresh();
    } catch (error) {
      console.error('Error saving:', error);
      const msg =
        error?.message ||
        (typeof error === 'string' ? error : JSON.stringify(error));
      notifyError(showNotify, p('alert_save_error', { message: msg }));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      name: record.name || '',
      code: record.code || '',
      description: record.description || '',
      manager: record.manager || '',
      location: record.location || '',
      email: record.email || '',
      phone: record.phone || '',
      budget: record.budget || '',
      budgetUsed: record.budgetUsed || '',
      employeeCapacity: record.employeeCapacity || '',
      establishedDate: record.establishedDate || '',
      status: record.status || 'active',
      notes: record.notes || ''
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/management/departments/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'القسم' }));
        refreshDepartmentsData();
        dispatchHrDepartmentsRefresh();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_delete_error', { defaultValue: 'تعذّر حذف القسم' }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/management/departments/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'قسم' }));
        setSelectedRecords([]);
        refreshDepartmentsData();
        dispatchHrDepartmentsRefresh();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_bulk_delete_error', { defaultValue: 'تعذّر حذف الأقسام المحددة' }));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      manager: '',
      location: '',
      email: '',
      phone: '',
      budget: '',
      budgetUsed: '',
      employeeCapacity: '',
      establishedDate: '',
      status: 'active',
      notes: ''
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  // Export/Import handlers
  const handleExport = () => {
    const headers = [
      { key: 'code', label: p('col_code') },
      { key: 'name', label: p('col_name') },
      { key: 'manager', label: p('col_manager') },
      { key: 'location', label: p('col_location') },
      { key: 'budget', label: p('col_budget') },
      { key: 'status', label: p('col_status') },
    ];
    
    const dataToExport = filteredDepartments;
    const fileName = `departments_export_${new Date().toISOString().split('T')[0]}`;
    
    try {
      exportToExcel(dataToExport, headers, `${fileName}.xlsx`);
    } catch (error) {
      console.error('Export error:', error);
      alert(p('alert_export_error'));
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const filteredData = filteredDepartments;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_code')}</th><th>${p('col_name')}</th><th>${p('col_manager')}</th><th>${p('col_location')}</th><th>${p('col_budget')}</th><th>${p('col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.code || p('na')}</td>
                  <td>${item.name || ''}</td>
                  <td>${item.manager || ''}</td>
                  <td>${item.location || ''}</td>
                  <td>${item.budget ? formatBrandMoney(parseFloat(item.budget) || 0) : formatBrandMoney(0)}</td>
                  <td>${item.status || p('na')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const filteredData = filteredDepartments;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_code')}</th><th>${p('col_name')}</th><th>${p('col_manager')}</th><th>${p('col_location')}</th><th>${p('col_budget')}</th><th>${p('col_employees')}</th><th>${p('col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.code || ''}</td>
                  <td>${item.name || ''}</td>
                  <td>${item.manager || ''}</td>
                  <td>${item.location || ''}</td>
                  <td>${item.budget ? parseFloat(item.budget).toLocaleString() : '0'}</td>
                  <td>${getEmployeeCount(item.name)}</td>
                  <td>${item.status || ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      document.body.appendChild(tempDiv);
      await generateReportExcel('departments', p('report_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('Excel generation error:', error);
      alert(p('alert_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'code', label: p('col_code') },
      { key: 'name', label: p('col_name') },
      { key: 'manager', label: p('col_manager') },
      { key: 'location', label: p('col_location') },
    ];
    
    const schema = {
      requiredFields: ['name'],
      validators: {
        status: (value) => !value || ['active', 'inactive'].includes(value)
      }
    };
    
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/management/departments', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await refreshDepartmentsData();
          const message = p('import_done', { total: result.totalCount, imported: successCount }) +
            (result.invalidCount > 0 ? p('import_errors', { count: result.invalidCount, details: result.errors.slice(0, 5).join('\n') }) : '');
          alert(message);
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };

  const statusOptions = useMemo(() => ([
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'inactive', label: p('status_inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ]), [p]);

  const departmentStatusFilterOptions = useMemo(
    () => statusOptions.map((s) => ({ value: s.value, label: s.label })),
    [statusOptions],
  );

  const listStats = useMemo(
    () => [
      { key: 'total', label: p('stat_total'), value: stats.total, icon: Building, variant: 'copper' },
      { key: 'active', label: p('stat_active'), value: stats.active, icon: CheckCircle, variant: 'forest' },
      { key: 'inactive', label: p('stat_inactive'), value: stats.inactive, icon: XCircle, variant: 'copper' },
      { key: 'employees', label: p('stat_total_employees'), value: stats.totalEmployees, icon: Users, variant: 'forest' },
    ],
    [stats, p],
  );

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildHrColumnVisibilityState(HR_TABLE_COLUMNS),
  );
  const qawaemColumns = useMemo(
    () => buildHrQawaemColumnDefs(
      HR_TABLE_COLUMNS.map((col) => (
        col.key === 'actions' ? { ...col, label: t('common.actions') } : col
      )),
      p,
      columnVisibility,
    ),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createHrHandleToggleColumn(setColumnVisibility, HR_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isHrColumnVisible(columnVisibility, key);
  const tableColSpan = hrTableColSpan(columnVisibility);

  const importExportProps = useMemo(
    () => buildHrImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      p,
      t,
      isGeneratingPDF,
      handlers: {
        onImport: handleImport,
        onExportCSV: handleExport,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      },
    }),
    [showImportDropdown, showExportDropdown, isGeneratingPDF, p, t],
  );

  const openCreateModal = () => {
    setEditingRecord(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      manager: '',
      location: '',
      email: '',
      phone: '',
      budget: '',
      budgetUsed: '',
      employeeCapacity: '',
      establishedDate: '',
      status: 'active',
      notes: '',
    });
    setFormTab('general');
    setShowModal(true);
  };

  const departmentFormTabs = useMemo(() => ([
    { id: 'general', label: p('section_basic_info'), icon: Building, iconClassName: 'text-blue-400' },
    { id: 'additional', label: p('section_budget_finances'), icon: Banknote, iconClassName: 'text-amber-400' },
  ]), [p]);

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      {mainViewMode === 'all_departments' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setQuickFilter(filter.id)}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
              >
                <filter.icon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button
              onClick={() => setActiveDistributionFilter(null)}
              className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
            >
              <XCircle size={14} />
              {p('reset_filter')}
            </button>
          )}
        </div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
          <button
            key={item.filterKey || item.name}
            onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))}
            className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
              activeDistributionFilter === item.name
                ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20'
                : 'border-white/10 hover:border-white/20 hover:bg-white/10'
            }`}
            title={p('filter_by_title', { name: item.name })}
          >
            <div className="flex justify-between items-center mb-2">
              <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
              <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
            </div>
            <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
              <div
                className={`h-3 rounded-full ${item.color} transition-all`}
                style={{
                  width: `${(() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })()}%`,
                }}
              />
            </div>
            <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>
              {(() => {
                const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
              })()}% {t('common.of_total')}
            </p>
          </button>
        ))}
        </div>
      </motion.div>
    </div>
  );

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/management/departments/${recordId}`, { status: newStatus });
      await refreshDepartmentsData();
      dispatchHrDepartmentsRefresh();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };


  return (
    <>
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Building}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={
          <HrBulkDeleteFilterAction
            count={selectedRecords.length}
            onDelete={handleBulkDelete}
            label={p('delete_count', { count: selectedRecords.length })}
          />
        }
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        columns={qawaemColumns}
        visibleColumnCount={qawaemVisibleColumnCount}
        totalColumnCount={qawaemColumns.length}
        onCreate={openCreateModal}
        createLabel={p('new_department')}
        importExport={importExportProps}
        headerActions={
          <button type="button" onClick={refreshDepartmentsData} className="refresh-button" title={t('common.refresh', { defaultValue: 'تحديث' })}>
            <RefreshCcw size={20} />
          </button>
        }
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={buildHrControlsStrip({
          qawaem,
          viewMode,
          onViewModeChange: setViewMode,
          isDashboardLightMode,
          searchTerm,
          onSearchChange: setSearchTerm,
          searchPlaceholder: p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) }),
          cardsTitle: t('common.view_cards', { defaultValue: 'بطاقات' }),
          tableTitle: t('common.view_table', { defaultValue: 'جدول' }),
        })}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
          <table className="w-full employees-table data-table">
            <thead className="glass-table-head">
              <tr>
                <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  
                  {isColVisible('code') && (
                  <TableColumnHeaderFilter
                    columnKey="code"
                    label={p('col_code')}
                    value={colFilters.code ?? ''}
                    onChange={(v) => setColFilter('code', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('name') && (
                  <TableColumnHeaderFilter
                    columnKey="name"
                    label={p('col_name')}
                    value={colFilters.name ?? ''}
                    onChange={(v) => setColFilter('name', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('manager') && (
                  <TableColumnHeaderFilter
                    columnKey="manager"
                    label={p('col_manager')}
                    value={colFilters.manager ?? ''}
                    onChange={(v) => setColFilter('manager', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('location') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_location')}</th>}
                  {isColVisible('employeeCount') && (
                  <TableColumnHeaderFilter
                    columnKey="employeeCount"
                    label={p('col_employees')}
                    value={colFilters.employeeCount ?? ''}
                    onChange={(v) => setColFilter('employeeCount', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('budget') && (
                  <TableColumnHeaderFilter
                    columnKey="budget"
                    label={p('col_budget')}
                    value={colFilters.budget ?? ''}
                    onChange={(v) => setColFilter('budget', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('createdDate') && (
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_created_date')}</th>
                  )}
                  {isColVisible('status') && (
                  <TableColumnHeaderFilter
                    columnKey="status"
                    label={p('col_status')}
                    kind="select"
                    options={departmentStatusFilterOptions}
                    value={colFilters.status ?? ''}
                    onChange={(v) => setColFilter('status', v)}
                    align="center"
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredDepartments.length > 0 ? (
                  filteredDepartments.map((record, index) => (
                    <tr 
                      key={record.id} 
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      
                      {isColVisible('code') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.code || p('em_dash')}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {p('id_prefix')} {record.id?.substring(0, 8) || p('na')}
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('name') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.name}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.description?.substring(0, 40) || p('no_description')}...
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('manager') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.manager || p('unassigned')}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.email || p('no_email')}
                          </span>
                      </div>
                    </td>
                      )}
                      {isColVisible('location') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.location || p('unspecified')}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.phone || p('no_phone')}
                          </span>
                        </div>
                    </td>
                      )}
                      {isColVisible('employeeCount') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Users size={14} className="text-white/50" />
                            {getEmployeeCount(record)}
                          </div>
                          <span className="text-white/50 text-xs mt-0.5">
                            {p('capacity_label')} {record.employeeCapacity || p('unlimited')}
                          </span>
                        </div>
                    </td>
                      )}
                      {isColVisible('budget') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Banknote size={14} className="text-white/50" />
                            {record.budget ? formatBudgetDisplay(record.budget) : p('zero_da')}
                          </div>
                          <span className="text-white/50 text-xs mt-0.5">
                            {p('used_label')} {record.budgetUsed ? formatBudgetDisplay(record.budgetUsed) : p('zero_da')}
                          </span>
                        </div>
                    </td>
                      )}
                      {isColVisible('createdDate') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Clock size={14} className="text-white/50" />
                            {record.establishedDate ? formatHrDate(record.establishedDate, i18n.language) : p('na')}
                          </div>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.establishedDate ? p('days_ago', { count: Math.floor((Date.now() - new Date(record.establishedDate).getTime()) / (1000 * 60 * 60 * 24)) }) : p('unknown_date')}
                          </span>
                        </div>
                    </td>
                      )}
                      {isColVisible('status') && (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        </div>
                    </td>
                      )}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                ))
              ) : (
                <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Building size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty')}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Card View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredDepartments.length > 0 ? (
              filteredDepartments.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Building size={20} className="text-blue-400" />
                          {record.name}
                        </h3>
                        <div className="flex">
                          <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        </div>
                        {record.code && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                            {record.code}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Informations de Base */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_basic_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_description')}</span> {record.description || p('na')}</div>
                            <div><span className="font-medium">{p('label_location')}</span> {record.location || p('na')}</div>
                            <div><span className="font-medium">{p('label_created_date')}:</span> {record.establishedDate ? new Date(record.establishedDate).toLocaleDateString(dateLocale) : p('na')}</div>
                            <div className="pt-2 border-t border-white/20">
                              <span className="font-medium text-white">{p('label_code')}</span>
                              <div className="text-white/90 text-xs mt-1">{record.code || p('undefined_code')}</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Responsable & Contact */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_manager_contact')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Briefcase size={16} className="text-blue-400" />
                              <span className="font-medium">{p('label_manager')}</span> <span className="text-white">{record.manager || p('unassigned')}</span>
                            </div>
                            <div><span className="font-medium">{p('label_email')}</span> {record.email || p('na')}</div>
                            <div><span className="font-medium">{p('label_phone')}</span> {record.phone || p('na')}</div>
                            <div className="pt-2 border-t border-white/20">
                              <span className="font-medium">{p('label_status')}</span>
                              <div className="text-white/90 text-xs mt-1">{record.status === 'active' ? p('status_active') : p('status_inactive')}</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Ressources */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_resources')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="p-2 bg-blue-500/10 rounded border border-blue-500/30">
                              <span className="font-medium text-blue-300 text-xs">{p('label_employees')}</span>
                              <p className="text-blue-200 text-xs mt-1">{p('card_employees_count', { count: getEmployeeCount(record) })}</p>
                            </div>
                            {record.employeeCapacity && (
                              <div><span className="font-medium">{p('label_capacity')}</span> {record.employeeCapacity}</div>
                            )}
                          </div>
                        </div>
                        
                        {/* Budget */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_budget_finances')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.budget ? (
                              <div><span className="font-medium">{p('label_budget_allocated')}</span> {formatBudgetDisplay(record.budget)}</div>
                            ) : (
                              <div><span className="font-medium">{p('label_budget')}</span> {p('budget_undefined')}</div>
                            )}
                            {record.budgetUsed ? (
                              <div className="p-2 bg-green-500/10 rounded border border-green-500/30">
                                <span className="font-medium text-green-300 text-xs">{p('label_budget_used')}</span>
                                <p className="text-green-200 text-sm mt-1 font-bold">{formatBudgetDisplay(record.budgetUsed)}</p>
                              </div>
                            ) : null}
                            {record.budget && record.budgetUsed && (
                              <div><span className="font-medium">{p('label_remaining')}</span> {formatBudgetDisplay(parseFloat(record.budget) - parseFloat(record.budgetUsed))}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Building size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* View Modal - Shows 100% of data (aligned with EmployeesPage view modal) */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => { setShowViewModal(false); setViewingRecord(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button onClick={() => { setShowViewModal(false); setViewingRecord(null); }} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Informations de base */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Building size={18} className="text-blue-400" />
                    {p('section_basic_info')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_code')}</p>
                      <p className="text-white font-medium">{viewingRecord.code || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_name')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.name}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                      <div className="flex"><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('field_description')}</p>
                      <p className="text-white font-medium">{viewingRecord.description || p('no_description')}</p>
                    </div>
                  </div>
                </div>

                {/* Gestion & responsable */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Briefcase size={18} className="text-green-400" />
                    {p('section_management')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_manager')}</p>
                      <p className="text-white font-medium">{viewingRecord.manager || p('unassigned')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_location')}</p>
                      <p className="text-white font-medium">{viewingRecord.location || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_created_date')}</p>
                      <p className="text-white font-medium">
                        {viewingRecord.establishedDate ? new Date(viewingRecord.establishedDate).toLocaleDateString(dateLocale) : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('field_email')}</p>
                      <p className="text-white font-medium">{viewingRecord.email || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('field_phone')}</p>
                      <p className="text-white font-medium">{viewingRecord.phone || p('na')}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Users size={18} className="text-blue-400" />
                      {p('section_hr_resources')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_employee_count')}</p>
                        <p className="text-white font-medium text-lg">{getEmployeeCount(viewingRecord)}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_max_capacity')}</p>
                        <p className="text-white font-medium">{viewingRecord.employeeCapacity || p('unlimited')}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Banknote size={18} className="text-amber-400" />
                      {p('section_budget_finances')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_budget_allocated_view')}</p>
                        <p className="text-white font-medium text-lg">
                          {viewingRecord.budget ? formatBudgetDisplay(viewingRecord.budget) : p('budget_undefined')}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_budget_used_view')}</p>
                        <p className="text-white font-medium">
                          {viewingRecord.budgetUsed ? formatBudgetDisplay(viewingRecord.budgetUsed) : p('zero_da')}
                        </p>
                      </div>
                      {viewingRecord.budget && viewingRecord.budgetUsed && (
                        <div>
                          <p className="text-white/60 text-sm mb-1">{p('label_budget_remaining')}</p>
                          <p className="text-white font-medium text-lg">
                            {formatBudgetDisplay(parseFloat(viewingRecord.budget) - parseFloat(viewingRecord.budgetUsed))}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {viewingRecord.notes && (
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <FileText size={18} className="text-violet-400" />
                      {p('section_notes')}
                    </h3>
                    <p className="text-white font-medium whitespace-pre-wrap text-sm">{viewingRecord.notes}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Create/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" 
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
          >
            {/* Fixed Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? p('modal_edit') : p('modal_create')}</h2>
              <div className="flex items-center gap-3">
              <button
                  type="submit" 
                  form="department-form" 
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {editingRecord ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                <X size={24} />
              </button>
              </div>
            </div>

            <HrFormModalTabBar
              tabs={departmentFormTabs}
              activeTab={formTab}
              onTabChange={setFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('modal_edit') : p('modal_create')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="department-form" onSubmit={handleSubmit}>
                {formTab === 'general' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>
                              {p('field_ref_code')}
                              <span className={`text-xs ml-2 ${isDashboardLightMode ? 'text-gray-400' : 'text-white/40'}`}>{p('auto_generated')}</span>
                            </label>
                            <input 
                              type="text" 
                              value={formData.code} 
                              onChange={(e) => setFormData({ ...formData, code: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={editingRecord ? '' : (getDepartmentCodePrefix(formData.name) ? suggestNextDepartmentCode(formData.name, departments) : p('ph_code_example'))}
                            />
                            <p className={`text-xs mt-1 ${isDashboardLightMode ? 'text-gray-400' : 'text-white/30'}`}>
                              {editingRecord
                                ? p('code_edit_hint')
                                : getDepartmentCodePrefix(formData.name)
                                  ? p('code_prefix_hint', { prefix: getDepartmentCodePrefix(formData.name), example: suggestNextDepartmentCode(formData.name, departments) })
                                  : p('code_name_hint')}
                            </p>
                </div>
                          <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{<RequiredLabelText text={p('field_name')} required />}</label>
                            <input 
                              type="text" 
                              required 
                              value={formData.name} 
                              onChange={(e) => {
                                const name = e.target.value;
                                setFormData((prev) => {
                                  if (editingRecord) {
                                    return { ...prev, name };
                                  }
                                  const code = name.trim()
                                    ? suggestNextDepartmentCode(name, departments)
                                    : '';
                                  return { ...prev, name, code };
                                });
                              }} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('ph_name')} 
                            />
                          </div>
                          <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('field_description')}</label>
                            <textarea 
                              value={formData.description} 
                              onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                              rows={3}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" 
                              placeholder={p('ph_description')} 
                            />
                          </div>
                          <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('field_created_date')}</label>
                            <CustomDatePicker
                              value={formData.establishedDate}
                              onChange={(value) => setFormData({ ...formData, establishedDate: value })}
                              placeholder={p('ph_select_date')}
                            />
                          </div>
                          <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('field_status')}</label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={[
                                { value: 'active', label: p('status_active') },
                                { value: 'inactive', label: p('status_inactive') }
                              ]}
                              placeholder={p('select')}
                            />
                          </div>
                          <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('field_capacity')}</label>
                            <CustomNumberInput
                              value={formData.employeeCapacity}
                              onChange={(value) => setFormData({ ...formData, employeeCapacity: value })}
                              min={0}
                              step={1}
                              placeholder="50"
                            />
                          </div>
                  </div>
                )}

                {formTab === 'general' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                          <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('field_manager')}</label>
                            <CustomSelect
                              value={formData.manager}
                              onChange={handleManagerSelect}
                              options={employees.map(emp => ({
                                value: `${emp.prenom} ${emp.nom}`,
                                label: `${emp.prenom} ${emp.nom} - ${emp.fonction || p('employee_role')}`
                              }))}
                              placeholder={p('ph_manager')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className={`text-xs mt-1 ${isDashboardLightMode ? 'text-gray-400' : 'text-white/30'}`}>
                              {employees.length > 0 
                                ? p('employees_available', { count: employees.length })
                                : p('manager_manual')}
                            </p>
                          </div>
                          <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('col_location')}</label>
                            <input 
                              type="text" 
                              value={formData.location} 
                              onChange={(e) => setFormData({ ...formData, location: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('ph_location')} 
                            />
                </div>
                          <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('field_email')}</label>
                            <input 
                              type="email" 
                              value={formData.email} 
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('ph_email')} 
                            />
                </div>
                          <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('field_phone')}</label>
                            <input 
                              type="tel" 
                              value={formData.phone} 
                              onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={phonePlaceholder} 
                            />
                          </div>
                        </div>
                )}

                {formTab === 'additional' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('field_budget_allocated')}</label>
                            <CustomNumberInput
                              value={formData.budget}
                              onChange={(value) => setFormData({ ...formData, budget: value })}
                              step={0.01}
                              min={0}
                              decimals={2}
                              placeholder="500000"
                            />
                  </div>
                          <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('field_budget_used')}</label>
                            <CustomNumberInput
                              value={formData.budgetUsed}
                              onChange={(value) => setFormData({ ...formData, budgetUsed: value })}
                              step={0.01}
                              min={0}
                              decimals={2}
                              placeholder="250000"
                            />
                </div>
                          <div>
                            <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('field_notes')}</label>
                            <textarea 
                              value={formData.notes} 
                              onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
                              rows={3}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" 
                              placeholder={p('ph_notes')} 
                            />
              </div>
            </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default DepartmentsPage;

